import React, { useEffect, useMemo, useState } from "react";
import { Info, Pencil, X } from "lucide-react";

import LoadingSpinner from "../../common/LoadingSpinner";
import { projectsAPI } from "../../../services/api";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { isProjectManager } from "../../../constants/projectRoles";
import { isSystemAdmin } from "../../../constants/systemRoles";

const isAdmin = (systemRole) => isSystemAdmin(systemRole);
const FINANCE_FIELDS = new Set(["budget", "contract_value", "currency"]);

const WEEKDAYS = [
  { value: 0, label: "Mon" },
  { value: 1, label: "Tue" },
  { value: 2, label: "Wed" },
  { value: 3, label: "Thu" },
  { value: 4, label: "Fri" },
  { value: 5, label: "Sat" },
  { value: 6, label: "Sun" },
];

const detectBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const parseHolidayDates = (value) =>
  String(value || "")
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const toNullIfEmpty = (v) => {
  if (v === undefined || v === null) return undefined; // omit
  if (typeof v === "string" && v.trim() === "") return null;
  return v;
};

const parseMoney = (v) => {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const cleaned = v.replace(/,/g, "").trim();
    if (cleaned === "") return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const normalizeDate = (v) => {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "string" && v.trim() === "") return null;
  return v;
};

const errorToString = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Request failed";
  if (typeof d === "string") return d;
  return JSON.stringify(d, null, 2);
};

// Helper for coordinates
const toFloatOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const t = String(v).trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

const FIELD_LABELS = {
  project_number: "Project Number",
  client: "Client",
  location: "Location",
  description: "Description",
  status: "Status",
  start_date: "Start Date",
  end_date: "End Date",
  completion_percentage: "Completion %",

  latitude: "Latitude",
  longitude: "Longitude",

  manager_name: "Manager Name",
  manager_email: "Manager Email",
  manager_phone: "Manager Phone",
  client_contact: "Client Contact",
  client_email: "Client Email",
  client_phone: "Client Phone",

  client_org: "Client Organization",
  client_address_line1: "Client Address Line",
  client_city_state_zip: "Clinet Zip Address",

  budget: "Budget",
  contract_value: "Contract Value",
  currency: "Currency",
  timezone: "Project Timezone",
  workday_start: "Workday Start",
  workday_end: "Workday End",
  working_days: "Working Days",
  holiday_dates: "Project Holidays",
  foreman_escalation_business_days: "Foreman → GF",
  gf_escalation_business_days: "GF → Supervisor",
};

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (key === "working_days") {
    const selected = new Set(Array.isArray(value) ? value.map(Number) : []);
    return WEEKDAYS.filter((day) => selected.has(day.value)).map((day) => day.label).join(", ") || "—";
  }
  if (key === "holiday_dates") {
    return Array.isArray(value) && value.length ? value.join(", ") : "None configured";
  }
  if (key === "foreman_escalation_business_days" || key === "gf_escalation_business_days") {
    return `${value} working day${Number(value) === 1 ? "" : "s"}`;
  }
  if (key === "budget" || key === "contract_value") {
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return n.toLocaleString();
  }
  return String(value);
};

const ProjectInfoView = ({ currentUser, projectRole }) => {
  const { activeProject, setActiveProject } = useProjectContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const canEdit = useMemo(() => {
    if (isAdmin(currentUser?.role)) return true;
    return isProjectManager(projectRole);
  }, [currentUser?.role, projectRole]);

  const load = async () => {
    if (!activeProject?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await projectsAPI.getOne(activeProject.id);
      setProject(res.data);
      setActiveProject((prev) => ({ ...(prev || {}), ...(res.data || {}) }));
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.detail ||
          e.message ||
          "Failed to load project details",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id]);

  const openEdit = () => {
    if (!project) return;

    const editable = {
      name: project.name || "",
      client: project.client || "",
      project_number: project.project_number || "",
      location: project.location || "",
      description: project.description || "",
      status: project.status || "active",
      start_date: project.start_date || "",
      end_date: project.end_date || "",
      completion_percentage: project.completion_percentage ?? 0,

      latitude:
        project.latitude !== null && project.latitude !== undefined
          ? String(project.latitude)
          : "",
      longitude:
        project.longitude !== null && project.longitude !== undefined
          ? String(project.longitude)
          : "",

      manager_name: project.manager_name || "",
      manager_email: project.manager_email || "",
      manager_phone: project.manager_phone || "",
      client_contact: project.client_contact || "",
      client_email: project.client_email || "",
      client_phone: project.client_phone || "",
      client_org: project.client_org || "",
      client_address_line1: project.client_address_line1 || "",
      client_city_state_zip: project.client_city_state_zip || "",

      timezone: project.timezone || detectBrowserTimezone(),
      workday_start: project.workday_start || "08:00",
      workday_end: project.workday_end || "17:00",
      working_days: Array.isArray(project.working_days) ? project.working_days.map(Number) : [0, 1, 2, 3, 4],
      holiday_dates: Array.isArray(project.holiday_dates) ? project.holiday_dates.join("\n") : "",
      foreman_escalation_business_days: project.foreman_escalation_business_days ?? 5,
      gf_escalation_business_days: project.gf_escalation_business_days ?? 2,

      ...(isAdmin(currentUser?.role)
        ? {
            budget: project.budget ?? "",
            contract_value: project.contract_value ?? "",
            currency: project.currency ?? "USD",
          }
        : {}),
    };

    setForm(editable);
    setEditOpen(true);
  };

  const save = async () => {
    if (!activeProject?.id) return;

    setSaving(true);
    setError(null);

    try {
      const payload = {
        name: toNullIfEmpty(form.name),
        client: toNullIfEmpty(form.client),
        project_number: toNullIfEmpty(form.project_number),
        location: toNullIfEmpty(form.location),
        description: toNullIfEmpty(form.description),
        status: toNullIfEmpty(form.status),

        start_date: normalizeDate(form.start_date),
        end_date: normalizeDate(form.end_date),

        completion_percentage:
          form.completion_percentage === "" ||
          form.completion_percentage === null
            ? null
            : Number(form.completion_percentage),

        latitude: toFloatOrNull(form.latitude),
        longitude: toFloatOrNull(form.longitude),

        manager_name: toNullIfEmpty(form.manager_name),
        manager_email: toNullIfEmpty(form.manager_email),
        manager_phone: toNullIfEmpty(form.manager_phone),

        client_contact: toNullIfEmpty(form.client_contact),
        client_email: toNullIfEmpty(form.client_email),
        client_phone: toNullIfEmpty(form.client_phone),
        client_org: toNullIfEmpty(form.client_org),
        client_address_line1: toNullIfEmpty(form.client_address_line1),
        client_city_state_zip: toNullIfEmpty(form.client_city_state_zip),

        timezone: toNullIfEmpty(form.timezone),
        workday_start: toNullIfEmpty(form.workday_start),
        workday_end: toNullIfEmpty(form.workday_end),
        working_days: Array.isArray(form.working_days) ? form.working_days.map(Number).sort((a, b) => a - b) : [],
        holiday_dates: parseHolidayDates(form.holiday_dates),
        foreman_escalation_business_days: Number(form.foreman_escalation_business_days || 5),
        gf_escalation_business_days: Number(form.gf_escalation_business_days || 2),

        budget: parseMoney(form.budget),
        contract_value: parseMoney(form.contract_value),
        currency: toNullIfEmpty(form.currency),
      };

      Object.keys(payload).forEach(
        (k) => payload[k] === undefined && delete payload[k],
      );

      if (!isAdmin(currentUser?.role)) {
        for (const k of FINANCE_FIELDS) delete payload[k];
      }

      const res = await projectsAPI.update(activeProject.id, payload);

      setProject(res.data);
      setActiveProject((prev) => ({ ...(prev || {}), ...(res.data || {}) }));
      setEditOpen(false);
    } catch (e) {
      console.error(e);
      setError(errorToString(e));
    } finally {
      setSaving(false);
    }
  };

  if (!activeProject) return null;
  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
        {error}
      </div>
    );
  }

  if (!project)
    return (
      <div className="text-sm text-gray-600">No project details available.</div>
    );

  const showFinance = isAdmin(currentUser?.role);

  // Latitude at top, longitude at bottom of Overview
  const sections = [
    {
      title: "Overview",
      keys: [
        "latitude", // top
        "project_number",
        "client",
        "location",
        "status",
        "start_date",
        "end_date",
        "completion_percentage",
        "longitude", // bottom
      ],
    },
    { title: "Description", keys: ["description"] },
    {
      title: "Contacts",
      keys: [
        "manager_name",
        "manager_email",
        "manager_phone",
        "client_contact",
        "client_email",
        "client_phone",
        "client_org",
        "client_address_line1",
        "client_city_state_zip",
      ],
    },
    {
      title: "Issue Escalation Schedule",
      keys: [
        "timezone",
        "workday_start",
        "workday_end",
        "working_days",
        "holiday_dates",
        "foreman_escalation_business_days",
        "gf_escalation_business_days",
      ],
    },
    ...(showFinance
      ? [
          {
            title: "Financials (Admin)",
            keys: ["budget", "contract_value", "currency"],
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Project Info</h2>
          <p className="text-gray-600">Project profile and key metadata</p>
        </div>
        <div className="flex items-center gap-3">
          <Info className="w-8 h-8 text-sky-500" />
          {canEdit && (
            <button
              onClick={openEdit}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-black"
            >
              <Pencil className="w-4 h-4" />
              Edit
            </button>
          )}
        </div>
      </div>

      {sections.map((s) => (
        <div key={s.title} className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {s.title}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {s.keys.map((k) => (
              <div
                key={k}
                className={k === "description" ? "md:col-span-2" : ""}
              >
                <div className="text-xs text-gray-500">
                  {FIELD_LABELS[k] || k}
                </div>
                <div className="text-sm text-gray-900 whitespace-pre-wrap">
                  {formatValue(k, project[k])}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white w-full max-w-3xl rounded-xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-800">
                  Edit Project
                </h3>
                <p className="text-sm text-gray-600">
                  Update project profile details
                </p>
              </div>
              <button
                onClick={() => setEditOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Project Name */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Project Name
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.name || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, name: e.target.value }))
                    }
                  />
                </div>

                {/* Client */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Client
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.client || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, client: e.target.value }))
                    }
                  />
                </div>

                {/* Project Number */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Project Number
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.project_number || ""}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        project_number: e.target.value,
                      }))
                    }
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Location
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.location || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, location: e.target.value }))
                    }
                  />
                </div>

                {/* Latitude */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Latitude
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    type="number"
                    step="0.000001"
                    value={form.latitude ?? ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, latitude: e.target.value }))
                    }
                  />
                </div>

                {/* Longitude */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Longitude
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    type="number"
                    step="0.000001"
                    value={form.longitude ?? ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, longitude: e.target.value }))
                    }
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Status
                  </label>
                  <select
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.status || "active"}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, status: e.target.value }))
                    }
                  >
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="on_hold">On Hold</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Completion % */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Completion %
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    type="number"
                    min={0}
                    max={100}
                    value={form.completion_percentage ?? 0}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        completion_percentage: Number(e.target.value || 0),
                      }))
                    }
                  />
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Start Date
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    type="date"
                    value={form.start_date || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, start_date: e.target.value }))
                    }
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    End Date
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    type="date"
                    value={form.end_date || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, end_date: e.target.value }))
                    }
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Description
                  </label>
                  <textarea
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.description || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, description: e.target.value }))
                    }
                    rows={4}
                  />
                </div>

                {/* Manager Name */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Manager Name
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.manager_name || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, manager_name: e.target.value }))
                    }
                  />
                </div>

                {/* Manager Email */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Manager Email
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.manager_email || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, manager_email: e.target.value }))
                    }
                  />
                </div>

                {/* Manager Phone */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Manager Phone
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.manager_phone || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, manager_phone: e.target.value }))
                    }
                  />
                </div>

                {/* Client Contact */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Client Contact
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.client_contact || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, client_contact: e.target.value }))
                    }
                  />
                </div>

                {/* Client Email */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Client Email
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.client_email || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, client_email: e.target.value }))
                    }
                  />
                </div>

                {/* Client Phone */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Client Phone
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.client_phone || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, client_phone: e.target.value }))
                    }
                  />
                </div>

                {/* Client Organization */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Client Organization
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.client_org || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, client_org: e.target.value }))
                    }
                  />
                </div>

                {/* Client Address Line */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Client Address Line
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.client_address_line1 || ""}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        client_address_line1: e.target.value,
                      }))
                    }
                  />
                </div>

                {/* Client Zip Address */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Clinet Zip Address
                  </label>
                  <input
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={form.client_city_state_zip || ""}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        client_city_state_zip: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="md:col-span-2 mt-2 border-t pt-4">
                  <div className="text-sm font-semibold text-gray-800">Issue Escalation Schedule</div>
                  <div className="mt-1 text-xs text-gray-500">Deadlines use this project timezone and only count configured working days.</div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Project Timezone</label>
                  <div className="flex gap-2">
                    <input className="border rounded-md px-3 py-2 text-sm w-full" value={form.timezone || "UTC"} onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))} placeholder="America/New_York" />
                    <button type="button" onClick={() => setForm((prev) => ({ ...prev, timezone: detectBrowserTimezone() }))} className="shrink-0 rounded-md border px-3 py-2 text-xs hover:bg-gray-50">Use detected</button>
                  </div>
                  <div className="mt-1 text-[11px] text-gray-400">Detected: {detectBrowserTimezone()}</div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs font-medium text-gray-500">Start
                    <input type="time" className="mt-1 border rounded-md px-3 py-2 text-sm w-full" value={form.workday_start || "08:00"} onChange={(e) => setForm((prev) => ({ ...prev, workday_start: e.target.value }))} />
                  </label>
                  <label className="block text-xs font-medium text-gray-500">End
                    <input type="time" className="mt-1 border rounded-md px-3 py-2 text-sm w-full" value={form.workday_end || "17:00"} onChange={(e) => setForm((prev) => ({ ...prev, workday_end: e.target.value }))} />
                  </label>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-2">Working Days</label>
                  <div className="flex flex-wrap gap-2">
                    {WEEKDAYS.map((day) => {
                      const selected = (form.working_days || []).map(Number).includes(day.value);
                      return (
                        <label key={day.value} className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-xs ${selected ? "border-orange-300 bg-orange-50 text-orange-800" : "border-gray-200 bg-white text-gray-600"}`}>
                          <input type="checkbox" checked={selected} onChange={() => setForm((prev) => ({ ...prev, working_days: selected ? (prev.working_days || []).filter((value) => Number(value) !== day.value) : [...(prev.working_days || []), day.value] }))} />
                          {day.label}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Holiday Dates</label>
                  <textarea rows={3} className="border rounded-md px-3 py-2 text-sm w-full" value={form.holiday_dates || ""} onChange={(e) => setForm((prev) => ({ ...prev, holiday_dates: e.target.value }))} placeholder={"2026-12-25\n2026-12-26"} />
                  <div className="mt-1 text-[11px] text-gray-400">One ISO date per line. These dates are excluded from escalation counting.</div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Foreman → GF working days</label>
                  <input type="number" min="1" max="30" className="border rounded-md px-3 py-2 text-sm w-full" value={form.foreman_escalation_business_days ?? 5} onChange={(e) => setForm((prev) => ({ ...prev, foreman_escalation_business_days: e.target.value }))} />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">GF → Supervisor working days</label>
                  <input type="number" min="1" max="30" className="border rounded-md px-3 py-2 text-sm w-full" value={form.gf_escalation_business_days ?? 2} onChange={(e) => setForm((prev) => ({ ...prev, gf_escalation_business_days: e.target.value }))} />
                </div>

                {isAdmin(currentUser?.role) && (
                  <>
                    {/* Budget */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Budget
                      </label>
                      <input
                        className="border rounded-md px-3 py-2 text-sm w-full"
                        value={form.budget ?? ""}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, budget: e.target.value }))
                        }
                      />
                    </div>

                    {/* Contract Value */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Contract Value
                      </label>
                      <input
                        className="border rounded-md px-3 py-2 text-sm w-full"
                        value={form.contract_value ?? ""}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            contract_value: e.target.value,
                          }))
                        }
                      />
                    </div>

                    {/* Currency */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Currency
                      </label>
                      <input
                        className="border rounded-md px-3 py-2 text-sm w-full"
                        value={form.currency || "USD"}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, currency: e.target.value }))
                        }
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t flex items-center justify-end gap-2 sticky bottom-0 bg-white">
              <button
                onClick={() => setEditOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={save}
                className={`px-4 py-2 rounded-lg text-white ${
                  saving ? "bg-gray-400" : "bg-orange-500 hover:bg-orange-600"
                }`}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectInfoView;
